import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ArrowLeftIcon } from 'assets/icons/arrow-left.svg';
import Button from 'components/Button/Button';
import Separator from 'components/Separator/Separator';
import { BUTTON_VARIANT } from 'constants/interfaces';

import Panel from '../../../components/Panel/Panel';
import SurveyAnswerPrioritiseView
  from './components/MaterialAssessmentSurveyAnswerPrioritise/SurveyAnswerPrioritiseView';
import SurveyAnswerPanel from './components/SurveyAnswerPanel';
import SurveyAnswerQuestion from './components/SurveyAnswerQuestion';

import type { IQuestionResponse } from '../../../store/slices/survey/interfaces/ISurveyRequests';
import type { ISurveyAnswerProps } from './interfaces/ISurveyAnswer';

import styles from './scss/SurveyAnswer.module.scss';

function MASurveyAnswerView({
  control,
  onCancel,
  questionData,
  standardTopicsData,
  currentStep,
  onNextStep,
  onPrevStep,
  onReview,
  isInsightFinished,
}: ISurveyAnswerProps) {
  const { t } = useTranslation();
  const name = questionData?.questionList.name || '';
  const title = questionData?.insightType || '';

  return (
    <DndProvider backend={HTML5Backend}>
      {isInsightFinished ? (
        <div className={styles.finished}>
          <p>{t('materialityAssessment.complete')}</p>
        </div>
      ) : (
        <div className={styles.root}>
          {currentStep > 0
            && (
              <div className={styles.backNavigation}>
                <Button
                  onClick={onPrevStep}
                  title={t('common.back')}
                  startIcon={<ArrowLeftIcon />}
                  className={styles.backButton}
                  variant={BUTTON_VARIANT.ghost}
                />
              </div>
            )}
          {currentStep > 0
            ? (
              <SurveyAnswerPrioritiseView
                control={control}
                standardTopicsData={standardTopicsData}
                currentStep={currentStep}
                onCancel={onCancel}
                onNextStep={onNextStep}
                onPrevStep={onPrevStep}
                onReview={onReview}
              />
            )
            : (
              <Panel className={styles.panel}>
                <div className={styles.wrapper}>
                  <SurveyAnswerPanel
                    title={title}
                    name={name}
                    description={questionData?.questionList.description}
                  />
                  {questionData?.questionList.questions.map((question: IQuestionResponse, index: number) => (
                    <SurveyAnswerQuestion
                      control={control}
                      index={index}
                      key={question.id}
                      question={question}
                    />
                  ))}
                </div>
                <Separator />
                <div className={styles.footer}>
                  <Button
                    title={t('common.cancel')}
                    className={styles.nextStep}
                    variant={BUTTON_VARIANT.secondary}
                    onClick={onCancel}
                  />
                  <Button
                    title={
                      standardTopicsData.length > 0
                        ? t('materialityAssessment.nextStep')
                        : t('common.markAsDone')
                    }
                    className={styles.nextStep}
                    variant={BUTTON_VARIANT.primary}
                    onClick={standardTopicsData.length > 0 ? () => onNextStep() : () => onReview()}
                  />
                </div>
              </Panel>
            )}
        </div>
      )}
    </DndProvider>
  );
}

export default MASurveyAnswerView;
